/**
 * Recognises PRIVATE KEY MATERIAL in a Vibe app env-var value.
 *
 * A Vibe env var is plaintext by construction: the row stores the literal
 * string, and `GET /api/vibe/apps/:appId/env` returns it verbatim to any caller
 * holding an org API key with `vibe:read`. That is the documented contract, not
 * a defect — env vars are configuration.
 *
 * Key material is therefore the one thing that must never land in one, and the
 * platform already has the place it belongs: a `VibeAppSecretRef` pointer plus a
 * staged value, sealed under the tenant's own KMS CMK and delivered to the
 * cluster's Vault by its agent. The control plane holds `kms:Encrypt` and NOT
 * `kms:Decrypt`, so a value written that way cannot be read back by us, by an
 * org API key, or by anything short of the tenant's own Vault.
 *
 * This module is the shared definition of "that is a key" so that the API
 * boundary (Zod) and the domain (`VibeAppEnvVar.create`) enforce ONE rule rather
 * than two that can drift — the same arrangement `VibeAppNameSchema` already has
 * with `VibeApp.create`.
 *
 * ## Why the detector is deliberately narrow
 *
 * It matches only things that are UNAMBIGUOUSLY private key material. It does
 * not try to be a general secret scanner, and it must not become one here:
 * `VibeEnvVarValueSchema` explicitly allows JSON blobs, URLs and opaque tokens,
 * so a heuristic on entropy or on `*_TOKEN`-shaped names would refuse working
 * configuration. A gate that refuses correct work gets removed, and then the
 * real thing flows again.
 *
 * What is caught is exactly what nothing legitimate puts in a config value:
 *
 *   - a PEM PRIVATE KEY block (`-----BEGIN [<alg>] PRIVATE KEY-----`), which
 *     covers RSA / EC / OPENSSH / ENCRYPTED / PGP, and is what sits inside the
 *     `private_key` field of every Google service-account JSON key;
 *   - a Google service-account key JSON, by its own canonical
 *     `"type": "service_account"` marker.
 *
 * `PUBLIC KEY` and `CERTIFICATE` blocks are NOT matched. They are public by
 * definition and belong in configuration.
 *
 * ## Base64, and why it is matched without decoding anything
 *
 * Wrapping a key in base64 to dodge newline handling is the single most common
 * way one reaches an env var, so catching the raw form alone would be a speed
 * bump rather than a gate — the first thing a refused caller tries is base64,
 * which would move the key somewhere strictly harder to find.
 *
 * `@nexus/types` is consumed by the browser bundle as well as the backend, so
 * it has neither `Buffer` nor a guaranteed `atob` to decode with. Instead each
 * marker is matched by the base64 of the marker itself. Base64 is positional —
 * the encoding of a substring depends on its offset modulo 3 — so each marker
 * yields THREE anchors, one per alignment, and any base64 payload containing
 * the marker contains at least one of them regardless of what precedes it.
 *
 * Each anchor is the INTERIOR of one alignment's encoding: the leading 4-char
 * group is dropped when it mixes in preceding bytes, and the trailing group is
 * dropped always, so every character of an anchor comes from a 3-byte group
 * that lies entirely inside the marker. That trim is deliberately conservative
 * — a shorter anchor matches more, never less.
 *
 * Do not hand-edit them. `env-var-secret-material.test.ts` asserts the two
 * properties that make them work, rather than re-deriving one particular
 * construction: every anchor is a substring of the marker's encoding at some
 * alignment (soundness), and every prefix/suffix combination around the marker
 * still yields a hit (completeness). A constant that drifts fails one or both.
 *
 * ## The base64 service-account check is a CONJUNCTION, and that is measured
 *
 * `service_account` ALONE is not evidence of a key, because it is a substring
 * of `service_account_email` and `service_accounts` — both ordinary config, and
 * the first is close to the very thing a refused caller is told to store here
 * instead. Base64-wrapping either one used to be refused with a message
 * instructing the reader to do what they had just done.
 *
 * So the base64 path additionally requires the `private_key` FIELD NAME. A real
 * key file always carries both; a config blob naming a service account carries
 * neither key nor field.
 *
 * This costs nothing in detection, and that is the measured part rather than
 * the hoped-for part: a real service-account key file embeds
 * `-----BEGIN PRIVATE KEY-----` inside its `private_key` value, so its base64
 * ALREADY trips the PEM anchors above on its own. The conjunction only decides
 * WHICH kind gets reported, which is why GCP is tested first — the more
 * specific message wins for a value that satisfies both.
 */

/** What was recognised. Never carries any part of the offending value. */
export const VIBE_ENV_VAR_SECRET_MATERIAL_KINDS = [
  "PEM_PRIVATE_KEY",
  "GCP_SERVICE_ACCOUNT_KEY"
] as const;

export type VibeEnvVarSecretMaterialKind = (typeof VIBE_ENV_VAR_SECRET_MATERIAL_KINDS)[number];

export interface VibeEnvVarSecretMaterialFinding {
  kind: VibeEnvVarSecretMaterialKind;
  /** True when the marker was found in base64 rather than in the clear. */
  base64Wrapped: boolean;
}

/**
 * `-----BEGIN PRIVATE KEY-----`, and every algorithm-qualified variant of it.
 *
 * The algorithm words are optional and repeatable (`RSA`, `EC`, `OPENSSH`,
 * `ENCRYPTED`, `PGP`); the trailing ` BLOCK` covers PGP's spelling. `PRIVATE`
 * is mandatory, which is what keeps `PUBLIC KEY` and `CERTIFICATE` out.
 */
const PEM_PRIVATE_KEY_HEADER = /-----BEGIN(?: [A-Z0-9]+)* PRIVATE KEY(?: BLOCK)?-----/;

/** Google's canonical marker for a service-account key file. */
const GCP_SERVICE_ACCOUNT_MARKER = /"type"\s*:\s*"service_account"/;

/**
 * The plaintext substrings the base64 anchors below encode. Exported so the
 * spec derives from the same source the anchors claim to come from, rather
 * than from a second copy that could drift from this one.
 */
export const VIBE_ENV_VAR_PEM_PRIVATE_KEY_MARKER = "PRIVATE KEY";
export const VIBE_ENV_VAR_GCP_SERVICE_ACCOUNT_MARKER = "service_account";
export const VIBE_ENV_VAR_GCP_PRIVATE_KEY_FIELD_MARKER = "private_key";

/** Base64 anchors, one per byte alignment. Asserted sound and complete in the spec. */
export const PEM_PRIVATE_KEY_BASE64_ANCHORS = ["UFJJVkFURSBL", "SVZBVEUg", "UklWQVRFIEtF"] as const;

export const GCP_SERVICE_ACCOUNT_BASE64_ANCHORS = [
  "c2VydmljZV9hY2Nv",
  "cnZpY2VfYWNjb3Vu",
  "ZXJ2aWNlX2FjY291"
] as const;

/**
 * The second half of the base64 conjunction — the `private_key` FIELD NAME,
 * lowercase, as it appears in the key file's JSON. Different bytes from the
 * uppercase `PRIVATE KEY` of the PEM header, so this is a genuinely
 * independent signal rather than the same evidence counted twice.
 */
export const GCP_PRIVATE_KEY_FIELD_BASE64_ANCHORS = ["cHJpdmF0", "aXZhdGVf", "cml2YXRl"] as const;

/**
 * Returns what was recognised, or `null` when the value is ordinary
 * configuration. Pure and allocation-light: a value is at most 32 KB
 * (`VibeEnvVarValueSchema`), and every check is a single scan.
 */
export function detectVibeEnvVarSecretMaterial(
  value: string
): VibeEnvVarSecretMaterialFinding | null {
  // GCP before PEM, here and in the base64 pair below. A service-account key
  // file satisfies BOTH — its `private_key` value is a PEM block — and the two
  // paths must not disagree about what the same key is. Naming the file rather
  // than the block inside it is also the more useful message.
  if (GCP_SERVICE_ACCOUNT_MARKER.test(value)) {
    return { kind: "GCP_SERVICE_ACCOUNT_KEY", base64Wrapped: false };
  }
  if (PEM_PRIVATE_KEY_HEADER.test(value)) {
    return { kind: "PEM_PRIVATE_KEY", base64Wrapped: false };
  }
  // GCP before PEM: a real key file satisfies both (its `private_key` value IS
  // a PEM block), and naming the more specific kind gives the better message.
  if (
    GCP_SERVICE_ACCOUNT_BASE64_ANCHORS.some((anchor) => value.includes(anchor)) &&
    GCP_PRIVATE_KEY_FIELD_BASE64_ANCHORS.some((anchor) => value.includes(anchor))
  ) {
    return { kind: "GCP_SERVICE_ACCOUNT_KEY", base64Wrapped: true };
  }
  if (PEM_PRIVATE_KEY_BASE64_ANCHORS.some((anchor) => value.includes(anchor))) {
    return { kind: "PEM_PRIVATE_KEY", base64Wrapped: true };
  }
  return null;
}

const KIND_DESCRIPTION: Record<VibeEnvVarSecretMaterialKind, string> = {
  PEM_PRIVATE_KEY: "a PEM private key block",
  GCP_SERVICE_ACCOUNT_KEY: "a Google service-account key"
};

/**
 * The refusal, written for whoever just ran the command.
 *
 * It never echoes the value, and it names the route that actually exists. Note
 * that the `nexus` CLI has no `vibe secret` subcommand today, so pointing at one
 * would send the reader hunting a command that is not there — the HTTP path is
 * the honest instruction until that lands.
 */
export function vibeEnvVarSecretMaterialMessage(finding: VibeEnvVarSecretMaterialFinding): string {
  const what = KIND_DESCRIPTION[finding.kind];
  const wrapped = finding.base64Wrapped ? ", base64-encoded" : "";
  return (
    `this value looks like ${what}${wrapped}, and env vars are stored and served in plaintext — ` +
    `any org API key with \`vibe:read\` can read it back. ` +
    `Register a secret instead: POST /api/vibe/apps/:appId/secrets to create the reference, ` +
    `then POST /api/vibe/apps/:appId/secrets/:secretRefId/value to set it. ` +
    `The value is sealed under your cluster's own KMS key and delivered to its Vault; ` +
    `the control plane cannot decrypt it. ` +
    `If this is genuinely not key material, store the non-secret identifier here and keep the key itself in a secret.`
  );
}
